// src/views/Admin/Locales/LocalPage/components/ActiveSessionCard/ActiveSessionCard.tsx

import React from 'react';
import { HiOutlineClock, HiOutlinePlay, HiOutlineUser } from 'react-icons/hi2';
import { FiCalendar, FiHash, FiLayers } from 'react-icons/fi';
import './ActiveSessionCard.scss';

interface Shift {
  shift_id: number;
  shift_name: string;
  state: boolean | number;
  user_name?: string;
}

interface ActiveSessionCardProps {
  isActive: boolean;
  sessionId?: number;
  startedBy?: string;
  startTime?: string;
  activeShiftName?: string;
  shifts?: Shift[];
  lastSessionDate?: string;
}

const ActiveSessionCard: React.FC<ActiveSessionCardProps> = ({
  isActive,
  sessionId,
  startedBy,
  startTime,
  activeShiftName,
  shifts = [],
  lastSessionDate
}) => {
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('es-CR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isActive) {
    return (
      <div className="session-card inactive">
        <div className="session-card-header">
          <div className="session-status">
            <HiOutlineClock size={20} />
            <span>Sin Sesión Activa</span>
          </div>
        </div>
        <div className="session-card-body">
          <div className="no-session-message">
            <p>Este local no tiene una sesión activa en este momento.</p>
            {lastSessionDate && (
              <span className="last-session">
                <FiCalendar size={14} />
                Última sesión: {formatDate(lastSessionDate)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="session-card active">
      <div className="session-card-header">
        <div className="session-status">
          <span className="pulse-dot" />
          <HiOutlinePlay size={18} />
          <span>Sesión Activa</span>
        </div>
        <div className="session-id">
          <FiHash size={14} />
          Sesión #{sessionId}
        </div>
      </div>
      
      <div className="session-card-body">
        <div className="session-details-grid">
          <div className="detail-item">
            <div className="detail-icon">
              <HiOutlineUser size={18} />
            </div>
            <div className="detail-content">
              <span className="detail-label">Iniciada por</span>
              <span className="detail-value">{startedBy || 'N/A'}</span>
            </div>
          </div>
          
          <div className="detail-item">
            <div className="detail-icon">
              <FiCalendar size={18} />
            </div>
            <div className="detail-content">
              <span className="detail-label">Inicio</span>
              <span className="detail-value">{formatDate(startTime)}</span>
            </div>
          </div>
          
          <div className="detail-item">
            <div className="detail-icon">
              <FiLayers size={18} />
            </div>
            <div className="detail-content">
              <span className="detail-label">Turno Activo</span>
              <span className="detail-value">{activeShiftName || 'N/A'}</span>
            </div>
          </div>
        </div>

        {shifts.length > 0 && (
          <div className="shifts-section">
            <div className="shifts-header">
              <span>Turnos ({shifts.length})</span>
            </div>
            <div className="shifts-list">
              {shifts.map((shift) => {
                const isShiftActive = shift.state === true || shift.state === 1;
                return (
                  <div 
                    key={shift.shift_id} 
                    className={`shift-chip ${isShiftActive ? 'active' : ''}`}
                  >
                    <span className="shift-name">{shift.shift_name}</span>
                    {isShiftActive && <span className="shift-badge">Activo</span>}
                    {shift.user_name && (
                      <span className="shift-user">{shift.user_name}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveSessionCard;
